import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import AuthLayout from './layouts/AuthLayout';
import DashboardLayout from './layouts/DashboardLayout';
import VendorLayout from './layouts/VendorLayout';
import ClientLayout from './layouts/ClientLayout';
import AuthCallback from './pages/auth/AuthCallback';
import RoleBasedRoute from './components/RoleBasedRoute';

import ProtectedRoute from './components/ProtectedRoute';
import FunctionBasedRoute from './components/FunctionBasedRoute';
import { LoadingState } from './components/common/LoadingState';
import { Toaster } from './components/ui/toaster';

// Import lazy components
import {
  Login,
  SignUp,
  ForgotPassword,
  ResetPassword,
  NewResetPassword,
  CupShupDashboard,
  VendorList,
  ClientList,
  ActivityList,
  ActivityDetail,
  MappedActivities,
  MappedTasks,
  InviteUser,
  ActivityForm,
  VendorDashboard,
  VendorActivityList,
  MyActivities,
  MyTasks,
  AddTask,
  VendorActivityDetail,
  ClientDashboard,
  ClientProfile,
  BrandTasks,
  TodoPage,
  Profile,
  VendorProfile,
  VendorProfileView,
  CupShupProfile,
  Settings,
  ClientTasks,
  // ClientActivities, // Old version - keeping for rollback if needed
  ClientActivitiesRedesigned,
  ClientAllTasks,
  ClientAITools,
  ClientAIAgents,
  ClientEmailCampaign,
  OperationsDashboard,
  ClientWhatsAppCampaign,
  MediaPlanning,
  MyPlans,
  CreativeStudioLanding,
  CampaignListPage,
  CreateCampaignWizard,
  CampaignDetailPage,
  CreativeDetailPage,
  ApprovalInboxPage,
  BrandGuidelinesSetupPage,
  RenderTestPage,
  NewPaymentRequestPage,
  MyRequestsPage,
  ReviewRequestsPage,
  RequestDetailPage,
  AutomationsPage,
  PersonalizedContentPage,
  NewsAnalyzerPage,
  SEOAuditPage,
  KeywordResearchPage,
  PLDashboard,
  ManagerCorrectionsDashboard,
  HRMSPage,
  SEOPage,
  BlogWritingService,
  ClientBlogDashboard,
  ClientBlogRequestForm,
  ClientBlogReviewPage
} from './routes/lazyComponents';

// Define LeadManagementPage here as it's not in the lazyComponents import
const LeadManagementPage = lazy(() => import('./pages/cupshup/LeadManagementPage'));
const InvoicingPage = lazy(() => import('./pages/cupshup/InvoicingPage'));
const InvoiceDashboard = lazy(() => import('./pages/invoicing/InvoiceDashboard'));

// Design Automation Agents
const UXResearcherPage = lazy(() => import('./pages/automations/UXResearcherPage'));
const WebDesignerPage = lazy(() => import('./pages/automations/WebDesignerPage'));
const MobileDesignerPage = lazy(() => import('./pages/automations/MobileDesignerPage'));
const OrganizationHierarchy = lazy(() => import('./pages/cupshup/OrganizationHierarchy'));

// Inactive User Page
const InactiveUserPage = lazy(() => import('./pages/common/InactiveUserPage'));

const App: React.FC = () => {
  console.log('[App Component] Rendering App');

  return (
    <>
      <Toaster />
      <div className="min-h-screen bg-gray-50">
        <Router>
          <Suspense fallback={<LoadingState />}>
            <Routes>
              <Route element={<AuthLayout />}>
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<SignUp />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/new-reset-password" element={<NewResetPassword />} />
                <Route path="/auth/callback" element={<AuthCallback />} />
              </Route>

              {/* Inactive User Page - accessible for authenticated users */}
              <Route element={<ProtectedRoute />}>
                <Route path="/inactive" element={<InactiveUserPage />} />
              </Route>

              {/* Catch-all dashboard route for email links */}
              <Route path="/dashboard" element={<RoleBasedRoute />} />

              {/* Role-based redirect */}
              <Route path="/" element={<RoleBasedRoute />} />

              {/* Common routes for authenticated users */}
              <Route element={<ProtectedRoute />}>
                <Route element={<DashboardLayout />}>
                  <Route path="settings" element={<Settings />} />
                  <Route path="profile" element={<Profile />} />
                </Route>
              </Route>

              {/* CupShup routes */}
              <Route element={<ProtectedRoute allowedRoles={['CupShup']} />}>
                <Route path="/cupshup" element={<DashboardLayout />}>
                  <Route index element={<Navigate to="dashboard" replace />} />
                  <Route path="dashboard" element={<CupShupDashboard />} />
                  <Route path="vendors" element={<VendorList />} />
                  <Route path="vendors/:vendorId" element={<VendorProfileView />} />
                  <Route path="clients" element={
                    <FunctionBasedRoute excludedFunctions={['Operation', 'Operations']}>
                      <ClientList />
                    </FunctionBasedRoute>
                  } />
                  <Route path="activities" element={<ActivityList />} />
                  <Route path="activities/:id" element={<ActivityDetail />} />
                  <Route path="mapped-activities" element={<MappedActivities />} />
                  <Route path="mapped-tasks" element={<MappedTasks />} />
                  <Route path="invite/vendor" element={<InviteUser type="vendor" />} />
                  <Route path="invite/client" element={<InviteUser type="client" />} />
                  <Route path="activities/new" element={<ActivityForm />} />
                  <Route path="operations" element={<OperationsDashboard />} />
                  <Route path="todo" element={<TodoPage />} />
                  <Route path="automations" element={<AutomationsPage />} />
                  <Route path="automations/personalized-content" element={<PersonalizedContentPage />} />
                  <Route path="automations/news-analyzer" element={<NewsAnalyzerPage />} />
                  <Route path="automations/seo-audit" element={<Navigate to="/cupshup/seo/site-audit" replace />} />
                  <Route path="automations/ux-researcher" element={<UXResearcherPage />} />
                  <Route path="automations/web-designer" element={<WebDesignerPage />} />
                  <Route path="automations/mobile-designer" element={<MobileDesignerPage />} />
                  {/* SEO Tools Routes - UNRESTRICTED FOR TESTING */}
                  <Route path="seo" element={<SEOPage />} />
                  <Route path="seo/blog-writing" element={<BlogWritingService />} />
                  <Route path="seo/keyword-research" element={<KeywordResearchPage />} />
                  <Route path="seo/site-audit" element={<SEOAuditPage />} />
                  <Route path="leads" element={<LeadManagementPage />} />
                  <Route path="invoicing" element={<InvoicingPage />} />
                  <Route path="invoice-dashboard" element={<InvoiceDashboard />} />
                  <Route path="pl" element={<PLDashboard />} />
                  <Route path="new-payment-request" element={<NewPaymentRequestPage />} />
                  <Route path="my-payment-requests" element={<MyRequestsPage />} />
                  <Route path="submitted-requests" element={<ReviewRequestsPage />} />
                  <Route path="request/:requestId" element={<RequestDetailPage />} />
                  <Route path="profile" element={<CupShupProfile />} />
                  <Route path="organization-hierarchy" element={<OrganizationHierarchy />} />
                  <Route path="hrms/*" element={<HRMSPage />} />
                </Route>
              </Route>

              {/* Vendor routes */}
              <Route element={<ProtectedRoute allowedRoles={['Vendor']} />}>
                <Route path="/vendor" element={<VendorLayout />}>
                  <Route index element={<VendorDashboard />} />
                  <Route path="activities" element={<VendorActivityList />} />
                  <Route path="my-activities" element={<MyActivities />} />
                  <Route path="my-tasks" element={<MyTasks />} />
                  <Route path="activities/:id" element={<VendorActivityDetail />} />
                  <Route path="activities/:id/tasks/new" element={<AddTask />} />
                  <Route path="profile" element={<VendorProfile />} />
                </Route>
              </Route>

              {/* Client routes */}
              <Route element={<ProtectedRoute allowedRoles={['Client']} />}>
                <Route path="/client" element={<ClientLayout />}>
                  <Route index element={<Navigate to="dashboard" replace />} />
                  <Route path="dashboard" element={<ClientDashboard />} />
                  <Route path="media-planning" element={<MediaPlanning />} />
                  <Route path="my-plans" element={<MyPlans />} />
                  <Route path="activities" element={<ClientActivitiesRedesigned />} />
                  <Route path="all-tasks" element={<ClientAllTasks />} />
                  <Route path="ai-tools" element={<ClientAITools />} />
                  <Route path="ai-agents" element={<ClientAIAgents />} />
                  <Route path="email-campaign" element={<ClientEmailCampaign />} />
                  <Route path="whatsapp-campaign" element={<ClientWhatsAppCampaign />} />
                  <Route path="profile" element={<ClientProfile />} />
                  <Route path="tasks" element={<BrandTasks />} />
                  <Route path="client-tasks" element={<ClientTasks />} />
                  <Route path="creative-studio" element={<CreativeStudioLanding />} />
                  <Route path="creative-studio/campaigns" element={<CampaignListPage />} />
                  <Route path="creative-studio/campaigns/new" element={<CreateCampaignWizard />} />
                  <Route path="creative-studio/campaigns/:id" element={<CampaignDetailPage />} />
                  <Route path="creative-studio/creatives/:id" element={<CreativeDetailPage />} />
                  <Route path="creative-studio/approvals" element={<ApprovalInboxPage />} />
                  <Route path="creative-studio/brand-setup" element={<BrandGuidelinesSetupPage />} />
                  <Route path="creative-studio/render-test" element={<RenderTestPage />} />
                  {/* SEO Tools for Clients - View Only */}
                  <Route path="seo" element={<SEOPage />} />
                  {/* Redirect old blog-writing route to new blog portal */}
                  <Route path="seo/blog-writing" element={<Navigate to="/client/blog" replace />} />
                  <Route path="seo/keyword-research" element={<KeywordResearchPage />} />
                  {/* Client Blog Portal */}
                  <Route path="blog" element={<ClientBlogDashboard />} />
                  <Route path="blog/new" element={<ClientBlogRequestForm />} />
                  <Route path="blog/:id" element={<ClientBlogReviewPage />} />
                </Route>
              </Route>

              {/* Redirect to todo page if no other route matches */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </Router>
      </div>
    </>
  );
};

export default App;